/**
 * 長形式の群／値列など、よくある内部キーを UI 言語の見出しへ揃える。
 */

import type { TFunction } from 'i18next';
import type { DataRow, VariableMapping } from '../stores/systems/keisuke/dashboardStore';
import { parseFiniteNumberFromCell } from './keisukeGridData';

export function isKeisukeGenericGroupColumnKey(key: string): boolean {
  const k = key.trim().toLowerCase();
  return k === 'group' || k === 'groups' || k === '群';
}

export function isKeisukeGenericValueColumnKey(key: string): boolean {
  const k = key.trim().toLowerCase();
  return k === 'value' || k === 'values' || k === 'measure' || k === '値';
}

export function isKeisukeWeightColumnKey(key: string): boolean {
  const k = key.trim().toLowerCase();
  return k === 'weight' || k === 'weight(g)' || k === '体重';
}

/** チャート軸・表ヘッダ共通: 内部キー → 表示ラベル */
export function keisukeStockColumnDisplayLabel(
  col: string,
  role: 'group' | 'value',
  t: TFunction
): string {
  const key = String(col ?? '').trim();
  if (!key) {
    return role === 'group'
      ? t('keisuke.chartAxisDefaults.xLabel')
      : t('keisuke.chartAxisDefaults.valueLabel');
  }
  if (isKeisukeGenericGroupColumnKey(key)) return t('keisuke.chartAxisDefaults.xLabel');
  if (isKeisukeGenericValueColumnKey(key)) return t('keisuke.chartAxisDefaults.valueLabel');
  if (isKeisukeWeightColumnKey(key)) return t('keisuke.chartAxisDefaults.weightLabel');
  return key;
}

/**
 * 対応なし t 検定フォーム → 長形式に書くときの列名。
 * 汎用 key（group/value）はロケール語にし、Weight 系は体重ラベルにする。
 */
export function resolveUnpairedLongFormatColumnKeys(
  t: TFunction,
  opts?: { groupKey?: string; valueKey?: string; valueAxisLabel?: string }
): { groupKey: string; valueKey: string } {
  const localeGroup = t('keisuke.chartAxisDefaults.xLabel');
  const localeValue = t('keisuke.chartAxisDefaults.valueLabel');
  const localeWeight = t('keisuke.chartAxisDefaults.weightLabel');

  const gRaw = opts?.groupKey?.trim() ?? '';
  const groupKey = gRaw && !isKeisukeGenericGroupColumnKey(gRaw) ? gRaw : localeGroup;

  const candidate = (opts?.valueAxisLabel?.trim() || opts?.valueKey?.trim() || '').trim();
  let valueKey: string;
  if (candidate && isKeisukeWeightColumnKey(candidate)) valueKey = localeWeight;
  else if (candidate && !isKeisukeGenericValueColumnKey(candidate)) valueKey = candidate;
  else valueKey = localeValue;

  return { groupKey, valueKey };
}

function isRemappableStockHeader(key: string): boolean {
  return (
    isKeisukeGenericGroupColumnKey(key) ||
    isKeisukeGenericValueColumnKey(key) ||
    isKeisukeWeightColumnKey(key)
  );
}

/**
 * 長形式の先頭列が stock 英語キーのとき、ロケール見出しへリネーム（行キー・mapping も同期）。
 * 変更不要なら null。
 */
export function remapStockLongFormatGrid(
  headers: string[],
  data: DataRow[],
  mapping: VariableMapping | undefined,
  t: TFunction
): { headers: string[]; data: DataRow[]; mapping: VariableMapping } | null {
  if (headers.length === 1) {
    const h0 = headers[0]!;
    if (!isRemappableStockHeader(h0) || isKeisukeGenericGroupColumnKey(h0)) return null;
    const valueKey = isKeisukeWeightColumnKey(h0)
      ? t('keisuke.chartAxisDefaults.weightLabel')
      : t('keisuke.chartAxisDefaults.valueLabel');
    if (valueKey === h0) return null;
    const nextData = data.map((row) => {
      const next: DataRow = { ...row };
      next[valueKey] = row[h0];
      delete next[h0];
      return next;
    });
    const nextMapping: VariableMapping = {
      ...(mapping ?? {}),
      valueColumn: valueKey,
    };
    delete nextMapping.valueAxisLabel;
    return { headers: [valueKey], data: nextData, mapping: nextMapping };
  }

  if (headers.length < 2) return null;
  const h0 = headers[0]!;
  const h1 = headers[1]!;
  if (!isRemappableStockHeader(h0) && !isRemappableStockHeader(h1)) return null;

  const { groupKey, valueKey } = resolveUnpairedLongFormatColumnKeys(t, {
    groupKey: h0,
    valueKey: h1,
    valueAxisLabel: mapping?.valueAxisLabel,
  });
  if (groupKey === h0 && valueKey === h1) return null;

  const nextHeaders = [...headers];
  nextHeaders[0] = groupKey;
  nextHeaders[1] = valueKey;

  const nextData = data.map((row) => {
    const next: DataRow = { ...row };
    if (groupKey !== h0) {
      next[groupKey] = row[h0];
      delete next[h0];
    }
    if (valueKey !== h1) {
      next[valueKey] = row[h1];
      delete next[h1];
    }
    return next;
  });

  const nextMapping: VariableMapping = {
    ...(mapping ?? {}),
    groupColumn: groupKey,
    valueColumn: valueKey,
  };
  if (isKeisukeGenericValueColumnKey(valueKey)) {
    if (mapping?.valueAxisLabel?.trim()) {
      nextMapping.valueAxisLabel = mapping.valueAxisLabel.trim();
    }
  } else {
    delete nextMapping.valueAxisLabel;
  }

  return { headers: nextHeaders, data: nextData, mapping: nextMapping };
}

/**
 * 「値の列」セレクト用: 数値がある列を優先し、群ラベル列は候補から外す。
 * 現在値が候補外でも先頭に残す（マッピング切れを防ぐ）。
 */
export function headersForValueColumnSelect(
  headers: string[],
  gridData: DataRow[],
  currentValue?: string
): string[] {
  const numeric = headers.filter((h) =>
    gridData.some((row) => parseFiniteNumberFromCell(row[h]) !== null)
  );
  const base = numeric.length > 0 ? numeric : headers;
  const withoutGroup = base.filter((h) => !isKeisukeGenericGroupColumnKey(h));
  const opts = withoutGroup.length > 0 ? withoutGroup : base;
  const cur = currentValue?.trim() ?? '';
  if (cur && !opts.includes(cur)) return [cur, ...opts];
  return opts;
}
